import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { sortedListRequest } from './common/list.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { queryBooleanSchema } from './common/query.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

export const regionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  pseudonym: z.string().nullable(),
  code: z.string(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
});

export const zoneSchema = z.object({
  id: z.uuid(),
  regionId: z.uuid(),
  name: z.string(),
  pseudonym: z.string().nullable(),
  code: z.string(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
});

export const countrySchema = z.object({
  id: z.uuid(),
  zoneId: z.uuid(),
  regionId: z.uuid(),
  name: z.string(),
  pseudonym: z.string().nullable(),
  isoCode: z.string().length(2),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
});

/**
 * The columns the region, zone and country lists order by. The tables hold tens
 * of rows — the geographic tree above the institutions — so no index is added
 * for the orderings; the default is the tree's own `sortOrder`, then the name.
 */
export const ORG_UNIT_SORT_FIELDS = ['sortOrder', 'name', 'code'] as const;
export type OrgUnitSortField = (typeof ORG_UNIT_SORT_FIELDS)[number];

export const orgUnitStatusFilterSchema = z.enum(['all', 'active', 'inactive']);
export type OrgUnitStatusFilter = z.infer<typeof orgUnitStatusFilterSchema>;

/**
 * A level's list screen: paginated, searched by name or code, filtered by
 * status — `active` by default, which is what every picker wants, since new
 * work under an inactive node is refused — and by parent.
 */
/**
 * `writable` says whether the reader's scope may reshape the tree at all —
 * global scope alone — so a screen offers no write the server would refuse
 * without deciding the rule itself.
 */
const levelListInput = sortedListRequest(ORG_UNIT_SORT_FIELDS, 'sortOrder', 'asc').extend({
  status: orgUnitStatusFilterSchema.default('active'),
});

/** What stands under a node, so a screen can say why deleting it will be refused before it is tried. */
const nodeCounts = {
  childCount: z.number().int().min(0),
  activeChildCount: z.number().int().min(0),
  institutionCount: z.number().int().min(0),
};

export const regionListItemSchema = regionSchema.extend(nodeCounts);
export const zoneListItemSchema = zoneSchema.extend({ regionName: z.string(), ...nodeCounts });
export const countryListItemSchema = countrySchema.extend({
  regionName: z.string(),
  zoneName: z.string(),
  ...nodeCounts,
});

export type Region = z.infer<typeof regionSchema>;
export type Zone = z.infer<typeof zoneSchema>;
export type Country = z.infer<typeof countrySchema>;
export type RegionListItem = z.infer<typeof regionListItemSchema>;
export type ZoneListItem = z.infer<typeof zoneListItemSchema>;
export type CountryListItem = z.infer<typeof countryListItemSchema>;

export const institutionOrganizationSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  /** The organization that speaks for a donation of the institution not yet paid. */
  isPrimary: z.boolean(),
  /**
   * Whether this institution is where every portal donation of the
   * organization's own site is born — one institution per organization
   * (docs/decisions/0054 *Amended 2026-09-18*, 0112 §1).
   */
  isHeadquarters: z.boolean(),
});

export type InstitutionOrganization = z.infer<typeof institutionOrganizationSchema>;

export const institutionSchema = z.object({
  id: z.uuid(),
  countryId: z.uuid(),
  zoneId: z.uuid(),
  regionId: z.uuid(),
  name: z.string(),
  pseudonym: z.string().nullable(),
  code: z.string(),
  isActive: z.boolean(),
  countryName: z.string(),
  zoneName: z.string(),
  regionName: z.string(),
  /** The organizations the institution is authorized for, the primary first. */
  organizations: z.array(institutionOrganizationSchema),
});

export type Institution = z.infer<typeof institutionSchema>;

export const treeNodeSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  code: z.string(),
  isActive: z.boolean(),
  level: z.enum(['region', 'zone', 'country', 'institution']),
  institutionCount: z.number().int(),
  children: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      code: z.string(),
      isActive: z.boolean(),
      level: z.enum(['region', 'zone', 'country', 'institution']),
      institutionCount: z.number().int(),
      children: z.array(
        z.object({
          id: z.uuid(),
          name: z.string(),
          code: z.string(),
          isActive: z.boolean(),
          level: z.enum(['region', 'zone', 'country', 'institution']),
          institutionCount: z.number().int(),
          children: z.array(
            z.object({
              id: z.uuid(),
              name: z.string(),
              code: z.string(),
              isActive: z.boolean(),
              level: z.enum(['region', 'zone', 'country', 'institution']),
              institutionCount: z.number().int(),
            }),
          ),
        }),
      ),
    }),
  ),
});

export const orgUnitNameSchema = z.string().trim().min(2).max(200);
export const pseudonymSchema = z.string().trim().min(2).max(80);

export const orgUnitCodeSchema = z
  .string()
  .trim()
  .min(2)
  .max(50)
  .regex(/^[A-Z0-9-]+$/, 'Uppercase letters, digits and - only');

const nameAndCode = {
  name: orgUnitNameSchema,
  code: orgUnitCodeSchema,
};

export const orgUnitContract = {
  tree: oc
    .route({
      method: 'GET',
      path: '/org-units/tree',
      summary: 'The hierarchy within the caller’s scope',
    })
    .errors(guarded)
    .input(z.object({ includeInactive: queryBooleanSchema.default(false) }))
    .output(z.object({ regions: z.array(treeNodeSchema) })),

  regions: {
    list: oc
      .route({ method: 'GET', path: '/org-units/regions' })
      .errors(guarded)
      .input(levelListInput)
      .output(paginated(regionListItemSchema).extend({ writable: z.boolean() })),
    create: oc
      .route({ method: 'POST', path: '/org-units/regions' })
      .errors(guarded)
      .input(
        z.object({
          ...nameAndCode,
          pseudonym: pseudonymSchema.nullable().optional(),
          sortOrder: z.number().int().default(0),
        }),
      )
      .output(z.object({ region: regionSchema })),
    update: oc
      .route({ method: 'PATCH', path: '/org-units/regions/{id}' })
      .errors(guarded)
      .input(
        z.object({
          id: z.uuid(),
          name: nameAndCode.name.optional(),
          pseudonym: pseudonymSchema.nullable().optional(),
          sortOrder: z.number().int().optional(),
          isActive: z.boolean().optional(),
        }),
      )
      .output(z.object({ region: regionSchema })),
    /**
     * Removes a region nothing names — global scope alone, behind
     * `orgUnit.delete`. One that a child node, a user, a role, a campaign, a
     * donation or any other row names is refused 409 `ORG_UNIT_REFERENCED`, and
     * is deactivated with `update` instead.
     */
    delete: oc
      .route({ method: 'DELETE', path: '/org-units/regions/{id}' })
      .errors(guarded)
      .input(z.object({ id: z.uuid() }))
      .output(z.object({ id: z.uuid(), deleted: z.literal(true) })),
  },

  zones: {
    list: oc
      .route({ method: 'GET', path: '/org-units/zones' })
      .errors(guarded)
      .input(levelListInput.extend({ regionId: z.uuid().optional() }))
      .output(paginated(zoneListItemSchema).extend({ writable: z.boolean() })),
    create: oc
      .route({ method: 'POST', path: '/org-units/zones' })
      .errors(guarded)
      .input(
        z.object({
          regionId: z.uuid(),
          ...nameAndCode,
          pseudonym: pseudonymSchema.nullable().optional(),
          sortOrder: z.number().int().default(0),
        }),
      )
      .output(z.object({ zone: zoneSchema })),
    update: oc
      .route({ method: 'PATCH', path: '/org-units/zones/{id}' })
      .errors(guarded)
      .input(
        z.object({
          id: z.uuid(),
          name: nameAndCode.name.optional(),
          pseudonym: pseudonymSchema.nullable().optional(),
          sortOrder: z.number().int().optional(),
          isActive: z.boolean().optional(),
        }),
      )
      .output(z.object({ zone: zoneSchema })),
    /**
     * Removes a zone nothing names — global scope alone, behind
     * `orgUnit.delete`. One that a child node, a user, a role, a campaign, a
     * donation or any other row names is refused 409 `ORG_UNIT_REFERENCED`, and
     * is deactivated with `update` instead.
     */
    delete: oc
      .route({ method: 'DELETE', path: '/org-units/zones/{id}' })
      .errors(guarded)
      .input(z.object({ id: z.uuid() }))
      .output(z.object({ id: z.uuid(), deleted: z.literal(true) })),
  },

  countries: {
    list: oc
      .route({ method: 'GET', path: '/org-units/countries' })
      .errors(guarded)
      .input(levelListInput.extend({ regionId: z.uuid().optional(), zoneId: z.uuid().optional() }))
      .output(paginated(countryListItemSchema).extend({ writable: z.boolean() })),
    create: oc
      .route({ method: 'POST', path: '/org-units/countries' })
      .errors(guarded)
      .input(
        z.object({
          zoneId: z.uuid(),
          name: nameAndCode.name,
          pseudonym: pseudonymSchema.nullable().optional(),
          isoCode: z
            .string()
            .trim()
            .length(2)
            .regex(/^[A-Z]{2}$/, 'Two uppercase letters'),
          sortOrder: z.number().int().default(0),
        }),
      )
      .output(z.object({ country: countrySchema })),
    update: oc
      .route({ method: 'PATCH', path: '/org-units/countries/{id}' })
      .errors(guarded)
      .input(
        z.object({
          id: z.uuid(),
          name: nameAndCode.name.optional(),
          pseudonym: pseudonymSchema.nullable().optional(),
          sortOrder: z.number().int().optional(),
          isActive: z.boolean().optional(),
        }),
      )
      .output(z.object({ country: countrySchema })),
    /**
     * Removes a country nothing names — global scope alone, behind
     * `orgUnit.delete`. One that a child node, a user, a role, a campaign, a
     * donation or any other row names is refused 409 `ORG_UNIT_REFERENCED`, and
     * is deactivated with `update` instead.
     */
    delete: oc
      .route({ method: 'DELETE', path: '/org-units/countries/{id}' })
      .errors(guarded)
      .input(z.object({ id: z.uuid() }))
      .output(z.object({ id: z.uuid(), deleted: z.literal(true) })),
  },

  institutions: {
    list: oc
      .route({ method: 'GET', path: '/org-units/institutions' })
      .errors(guarded)
      .input(
        paginationInputSchema.extend({
          search: z.string().trim().optional(),
          countryId: z.uuid().optional(),
          zoneId: z.uuid().optional(),
          regionId: z.uuid().optional(),
          organizationId: z.uuid().optional(),
          includeInactive: queryBooleanSchema.default(false),
        }),
      )
      .output(paginated(institutionSchema)),
    get: oc
      .route({ method: 'GET', path: '/org-units/institutions/{id}' })
      .errors(guarded)
      .input(z.object({ id: z.uuid() }))
      .output(z.object({ institution: institutionSchema })),
    create: oc
      .route({ method: 'POST', path: '/org-units/institutions' })
      .errors(guarded)
      .input(
        z.object({
          countryId: z.uuid(),
          ...nameAndCode,
        }),
      )
      .output(z.object({ institution: institutionSchema })),
    update: oc
      .route({ method: 'PATCH', path: '/org-units/institutions/{id}' })
      .errors(guarded)
      .input(
        z.object({
          id: z.uuid(),
          name: nameAndCode.name.optional(),
          pseudonym: pseudonymSchema.nullable().optional(),
          countryId: z.uuid().optional(),
          isActive: z.boolean().optional(),
        }),
      )
      .output(z.object({ institution: institutionSchema })),
    /**
     * Adds an organization the institution is authorized for; the first link is
     * its primary (docs/decisions/0112 §1). Global scope alone, behind
     * `organization.edit`, because it decides who may reconcile the
     * organization. 422 `INSTITUTION_ORGANIZATION_INACTIVE` for an inactive
     * organization; linking one already linked changes nothing.
     */
    linkOrganization: oc
      .route({ method: 'POST', path: '/org-units/institutions/{id}/organizations' })
      .errors(guarded)
      .input(z.object({ id: z.uuid(), organizationId: z.uuid() }))
      .output(z.object({ institution: institutionSchema })),
    /**
     * Removes an organization from the institution. Refused 409
     * `INSTITUTION_ORGANIZATION_HAS_DONATIONS` while a donation of the
     * institution is attributed to it or holds an active payment link on one of
     * its accounts, 409 `INSTITUTION_PRIMARY_ORGANIZATION_REQUIRED` for the
     * primary while other links remain, and 409
     * `INSTITUTION_ORGANIZATION_IS_HEADQUARTERS` for a headquarters.
     */
    unlinkOrganization: oc
      .route({
        method: 'DELETE',
        path: '/org-units/institutions/{id}/organizations/{organizationId}',
      })
      .errors(guarded)
      .input(z.object({ id: z.uuid(), organizationId: z.uuid() }))
      .output(z.object({ institution: institutionSchema })),
    /**
     * Makes an already-linked organization the institution's primary, the one
     * that speaks for a donation not yet paid. 422
     * `INSTITUTION_ORGANIZATION_NOT_LINKED` for one that is not linked.
     */
    setPrimaryOrganization: oc
      .route({ method: 'PUT', path: '/org-units/institutions/{id}/primary-organization' })
      .errors(guarded)
      .input(z.object({ id: z.uuid(), organizationId: z.uuid() }))
      .output(z.object({ institution: institutionSchema })),

    /**
     * Which institution of an organization every portal donation lands in.
     * The same route's gate — `organization.edit` at global scope alone — and
     * the same screen, because it is the same decision about an institution's
     * place in a legal entity, never inferred and never defaulted to the first
     * institution created (docs/decisions/0054 *Amended 2026-09-18*). It names
     * the organization, because an institution may be the headquarters of more
     * than one; naming one demotes that organization's previous headquarters.
     */
    setHeadquarters: oc
      .route({ method: 'PUT', path: '/org-units/institutions/{id}/headquarters' })
      .errors(guarded)
      .input(z.object({ id: z.uuid(), organizationId: z.uuid(), isHeadquarters: z.boolean() }))
      .output(z.object({ institution: institutionSchema })),
    /**
     * Removes a institution nothing names — global scope alone, behind
     * `orgUnit.delete`. One that a child node, a user, a role, a campaign, a
     * donation or any other row names is refused 409 `ORG_UNIT_REFERENCED`, and
     * is deactivated with `update` instead.
     */
    delete: oc
      .route({ method: 'DELETE', path: '/org-units/institutions/{id}' })
      .errors(guarded)
      .input(z.object({ id: z.uuid() }))
      .output(z.object({ id: z.uuid(), deleted: z.literal(true) })),
  },
};
